import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, Settings, LogOut, UserCircle, LayoutDashboard, BookOpen, Trophy, Users, Shield, GraduationCap, MessageSquare, HelpCircle, Bell, LifeBuoy } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { useProfile } from "@/hooks/useProfile";

interface ProfileMenuProps {
  user: any;
}

export function ProfileMenu({ user }: ProfileMenuProps) {
  const navigate = useNavigate();
  const { isAdmin, isInstructor, isLoading: rolesLoading } = useUserRole();
  const { t } = useTranslation();
  const { data: profile, isLoading: isLoadingProfile } = useProfile(user?.id);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      toast({
        title: t("common.error"),
        description: t("profile.logoutError"),
        variant: "destructive",
      });
      return;
    }

    toast({
      title: t("common.success"),
      description: t("profile.logoutSuccess"),
    });
    
    navigate("/");
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Show skeleton during initial profile load
  if (isLoadingProfile) {
    return (
      <div className="relative rounded-full">
        <Skeleton className="w-10 h-10 rounded-full" />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="relative rounded-full hover:ring-2 hover:ring-primary/50 transition-all duration-200 focus-visible:outline-none focus-visible:ring-0"
          aria-label="User menu"
        >
          <Avatar className="w-10 h-10 border-2 border-primary/20 cursor-pointer">
            <AvatarImage
              src={profile?.avatar_url || user?.user_metadata?.avatar_url || ""}
              alt={profile?.full_name || user?.user_metadata?.full_name || "User"}
            />
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {getInitials(profile?.full_name || user?.user_metadata?.full_name || "User")}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 bg-background/95 backdrop-blur-xl border glass-border shadow-lg z-[100] max-h-[80vh] overflow-y-auto"
      >
        <div className="px-3 py-2 min-h-[3rem]">
          <p className="text-sm font-medium">
            {profile?.full_name || user?.user_metadata?.full_name || "User"}
          </p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        
        <DropdownMenuSeparator className="glass-surface-strong" />
        
        {/* Role-based Dashboard Links - Pre-allocate space to prevent shifts */}
        <div className="min-h-[2.5rem]">
          {rolesLoading ? (
            <div className="px-2 py-1.5">
              <Skeleton className="h-9 w-full mb-1" />
            </div>
          ) : (
            <>
              {isAdmin && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/admin" className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>{t("profile.adminDashboard")}</span>
                  </Link>
                </DropdownMenuItem>
              )}
              {isInstructor && !isAdmin && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/instructor/dashboard" className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    <span>{t("profile.instructorDashboard")}</span>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to="/dashboard" className="flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" />
                  <span>{t("profile.myDashboard")}</span>
                </Link>
              </DropdownMenuItem>
            </>
          )}
        </div>
        
        {!rolesLoading && <DropdownMenuSeparator className="glass-surface-strong" />}
        
        {/* Main Navigation Links */}
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/courses" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span>{t("profile.courses")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/messages" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            <span>{t("profile.messages")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/gamification" className="flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            <span>{t("profile.gamification")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/community" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>{t("profile.community")}</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="glass-surface-strong" />
        
        {/* Announcements & Support */}
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/announcements" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <span>{t("profile.announcements")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/support" className="flex items-center gap-2">
            <LifeBuoy className="w-4 h-4" />
            <span>{t("profile.support")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/faq" className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            <span>{t("profile.faq")}</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="glass-surface-strong" />
        
        {/* Profile Management */}
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/profile" className="flex items-center gap-2">
            <UserCircle className="w-4 h-4" />
            <span>{t("profile.viewProfile")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/profile/edit" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{t("profile.editProfile")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span>{t("profile.settings")}</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="glass-surface-strong" />
        
        {/* Logout */}
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer hover:bg-destructive/20 text-destructive focus:text-destructive focus:bg-destructive/20"
        >
          <LogOut className="w-4 h-4 mr-2" />
          <span>{t("profile.logOut")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
